import { Box, Accordion, Pagination, Button, Group } from '@mantine/core';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import hasInterestsLogo from '../assets/img/discovery_prompt_has_interests.svg';
import noInterestsLogo from '../assets/img/discovery_prompt_no_interests.svg';
import DiscoveryItem from './DiscoveryItem';
import EmptyPrompt from './EmptyPrompt';
import TagList from './TagList';
import { theme } from '../consilitheme';
import Delay from './Delay';
import { useGetInsightsByPageQuery } from '../endpoints/discovery';
import { IconReload } from '@tabler/icons-react';

const DiscoveryManager = ({
  setIsEditing,
  setShowChatLogo,
  firstOpen,
  interestsData,
  isInterestsLoading,
}) => {
  const [activePage, setPage] = useState(1);

  const {
    data: insightsData,
    isLoading: isInsightsLoading,
    refetch: refetchInsights,
  } = useGetInsightsByPageQuery(activePage);

  const [localDiscoveries, setLocalDiscoveries] = useState([]);
  const [activeTopics, setActiveTopics] = useState([]);
  const beginDiscoveries =
    (interestsData?.length === 0 || !interestsData) &&
    !isInsightsLoading &&
    !isInterestsLoading;
  const analyzingInsights =
    !insightsData?.results?.length &&
    interestsData?.length &&
    !isInsightsLoading;
  const loadingInsights = isInsightsLoading;
  const defaultValue = localDiscoveries?.length
    ? `${localDiscoveries[0]?.body}-${localDiscoveries[0]?.id}`
    : undefined;
  const auth = useSelector((state) => state.auth);
  const [isDemo, setIsDemo] = useState(auth.demoAnimation && firstOpen);

  useEffect(() => {
    if (insightsData?.results.length) {
      setLocalDiscoveries(insightsData.results);
    }
  }, [insightsData, interestsData]);

  useEffect(() => {
    if (activeTopics && activeTopics.length > 0) {
      const filteredData = activeTopics.flatMap((tag) =>
        insightsData?.filter((insight) =>
          insight.categories.some((category) => category.name === tag)
        )
      );

      const combinedResults = filteredData.filter(
        (value, index, self) =>
          index === self.findIndex((item) => item.id === value.id)
      );
      setLocalDiscoveries(combinedResults);
    } else {
      setLocalDiscoveries(insightsData?.results);
    }
  }, [activeTopics, insightsData]);

  const handleGetStartedClick = () => {
    setIsEditing(true);
    setShowChatLogo();
  };

  const removeDiscoveryTag = (tag) => {
    setActiveTopics(activeTopics.filter((activeTag) => activeTag !== tag));
    setIsDemo(false);
  };

  const totalPages =
    insightsData?.count % 50 > 0 && insightsData?.count > 50
      ? insightsData?.count / 50 + 1
      : insightsData?.count / 50;
  return (
    <Box>
      {activeTopics.length !== 0 && (
        <Box mb={theme.padding.lg} ml="sm">
          <TagList
            baseTags={activeTopics}
            activeTopics={activeTopics}
            setActiveTopics={setActiveTopics}
            headerTags={true}
            clickable
            tagColor={theme.colors.purple[3]}
            removeTag={(tag, tagType) => removeDiscoveryTag(tag, tagType)}
          />
        </Box>
      )}
      {beginDiscoveries && (
        <EmptyPrompt
          svgLink={noInterestsLogo}
          description="Click here to begin your knowledge discovery campaign"
          onClick={handleGetStartedClick}
        />
      )}
      {analyzingInsights > 0 && (
        <EmptyPrompt
          svgLink={hasInterestsLogo}
          description="Consili is analyzing your sources for insights. You’ll be notified of any new developments"
          onClick={handleGetStartedClick}
        />
      )}
      {loadingInsights && (
        <EmptyPrompt
          svgLink={hasInterestsLogo}
          description="Consili is loading insights. Please wait."
          onClick={handleGetStartedClick}
        />
      )}
      {defaultValue && (
        <div style={{ flexDirection: 'column', alignItems: 'center' }}>
          <Accordion
            chevronPosition="right"
            variant="contained"
            defaultValue={defaultValue}
          >
            <Group style={{ justifyContent: 'space-between' }}>
              {totalPages > 1 && (
                <Pagination
                  total={totalPages}
                  value={activePage}
                  onChange={(page) => {
                    setPage(page);
                    setIsDemo(false);
                  }}
                  mb="sm"
                  withEdges={totalPages > 10}
                  size="sm"
                />
              )}
              <Button
                leftSection={<IconReload size={14} />}
                ml="auto"
                mb={5}
                size="xs"
                variant="default"
                onClick={() => refetchInsights(activePage)}
              >
                Refresh
              </Button>
            </Group>
            {localDiscoveries?.length > 0 &&
              interestsData?.length > 0 &&
              localDiscoveries.map((discovery) => {
                return (
                  <Accordion.Item
                    key={discovery.id}
                    value={`${discovery.body}-${discovery.id}`}
                    style={{ borderColor: 'transparent' }}
                    mt={10}
                  >
                    {isDemo ? (
                      <Delay
                        key={discovery.id}
                        waitBeforeShow={
                          discovery === localDiscoveries[0]
                            ? 100
                            : Math.floor(Math.random() * (1000 - 100 + 1)) +
                              1000
                        }
                      >
                        <DiscoveryItem
                          key={discovery.id}
                          discovery={discovery}
                          relatedInterest={
                            interestsData.find(
                              (interest) => interest.id === discovery.interest
                            )?.subject
                          }
                          activeTopics={activeTopics}
                          setActiveTopics={setActiveTopics}
                          removeTag={() => {}}
                          title={
                            discovery.highlight
                              ? discovery.highlight
                              : interestsData.find(
                                  (interest) =>
                                    interest.id === discovery.interest
                                )?.subject
                          }
                        />
                      </Delay>
                    ) : (
                      <DiscoveryItem
                        key={discovery.id}
                        discovery={discovery}
                        relatedInterest={
                          interestsData.find(
                            (interest) => interest.id === discovery.interest
                          )?.subject
                        }
                        activeTopics={activeTopics}
                        setActiveTopics={setActiveTopics}
                        removeTag={() => {}}
                        title={
                          discovery.highlight
                            ? discovery.highlight
                            : interestsData.find(
                                (interest) => interest.id === discovery.interest
                              )?.subject
                        }
                      />
                    )}
                  </Accordion.Item>
                );
              })}
            {totalPages > 1 && !isDemo && (
              <Pagination
                total={totalPages}
                value={activePage}
                onChange={(page) => {
                  setPage(page);
                  setIsDemo(false);
                }}
                mt="sm"
                withEdges={totalPages > 10}
                size="sm"
              />
            )}
          </Accordion>
        </div>
      )}
    </Box>
  );
};

export default DiscoveryManager;
